import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import {
    DEVICE_ID_KEY,
    JWT_TOKEN_KEY,
    REFRESH_TOKEN_KEY,
    USER_DATA_KEY,
} from '../../constants/auth';

import { BASE_URL } from '../../constants/api';
import dayjs from 'dayjs';
import { refreshToken } from '../../utils/refreshToken';
import { jwtDecode } from 'jwt-decode';
import { UserRole } from '../../constants/user';
import { removeSessionStorage } from '../../utils/sessionStorage';
import {
    clearTokenLocalStorage,
    getLocalStorage,
    setLocalStorage,
} from '../../utils/localStorage';

class AxiosService {
    private axiosInstance: AxiosInstance;
    private JWT: string | null;
    private REFRESH_TOKEN: string | null;
    private DEVICE_ID: string | null;
    private static instance: AxiosService;

    constructor() {
        this.JWT = getLocalStorage(JWT_TOKEN_KEY);
        this.REFRESH_TOKEN = getLocalStorage(REFRESH_TOKEN_KEY);
        this.DEVICE_ID = getLocalStorage(DEVICE_ID_KEY);

        // console.log(this.JWT, this.REFRESH_TOKEN, this.DEVICE_ID);

        this.axiosInstance = axios.create({
            baseURL: BASE_URL,
            headers: {},
            withCredentials: true,
            timeout: 10000,
        });

        this.axiosInstance.interceptors.request.use(
            async (config) => {
                // Preserve custom timeout if provided (overrides instance default)
                const customTimeout = config.timeout;
                
                // Nếu thiếu thông tin cần thiết thì trả về config gốc
                if (!this.DEVICE_ID || !this.REFRESH_TOKEN || !this.JWT) {
                    // this.clearData();
                    return config;
                }

                // Giải mã JWT để lấy thông tin exp và uid
                const decodedJWT = jwtDecode<{
                    exp: number;
                    uid: string;
                    role: UserRole;
                }>(this.JWT);

                // Nếu JWT đã hết hạn
                if (dayjs().isAfter(dayjs.unix(decodedJWT.exp))) {
                    // Giải mã refresh token
                    const decodedRefreshToken = jwtDecode<{
                        exp: number;
                        uid: string;
                        role: UserRole;
                    }>(this.REFRESH_TOKEN);

                    // Nếu refresh token cũng hết hạn, trả về config mà không cập nhật header
                    if (dayjs().isAfter(dayjs.unix(decodedRefreshToken.exp))) {
                        // this.clearData();
                        return config;
                    }

                    // Gọi refreshToken để lấy token mới (await vì refreshToken là async)
                    const data = await refreshToken({
                        jwt: this.JWT,
                        refreshToken: this.REFRESH_TOKEN,
                        deviceId: this.DEVICE_ID,
                        clientId: decodedJWT.uid,
                    });

                    // Nếu có dữ liệu trả về, cập nhật token và header Authorization
                    if (data) {
                        this.JWT = data.accessToken;
                        this.REFRESH_TOKEN = data.refreshToken;
                        config.headers['rf-token'] =
                            `Bearer ${this.REFRESH_TOKEN}`;
                        config.headers.Authorization = `Bearer ${this.JWT}`;
                        config.headers['device-id'] = this.DEVICE_ID;
                        setLocalStorage(JWT_TOKEN_KEY, this.JWT);
                        setLocalStorage(REFRESH_TOKEN_KEY, this.REFRESH_TOKEN);
                    }
                }
                // Nếu JWT chưa hết hạn, đảm bảo gửi kèm header Authorization
                config.headers.Authorization = `Bearer ${this.JWT}`;
                config.headers['rf-token'] = `Bearer ${this.REFRESH_TOKEN}`;
                config.headers['device-id'] = this.DEVICE_ID;

                // Restore custom timeout if it was provided
                if (customTimeout !== undefined) {
                    config.timeout = customTimeout;
                }

                return config;
            },
            (error) => Promise.reject(error)
        );

        this.axiosInstance.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error?.response?.status === 401) {
                    this.clearData();
                }
                return Promise.reject(error);
            }
        );
    }

    private clearData() {
        clearTokenLocalStorage();
        removeSessionStorage(USER_DATA_KEY);
        window.location.href = '/login';
    }

    post(url: string, payload?: any, config?: AxiosRequestConfig<any>) {
        return this.axiosInstance.post(url, payload, config);
    }

    get(url: string, config?: AxiosRequestConfig<any>) {
        return this.axiosInstance.get(url, config);
    }

    put(url: string, payload: any, config?: AxiosRequestConfig<any>) {
        return this.axiosInstance.put(url, payload, config);
    }

    delete(url: string, config?: AxiosRequestConfig<any>) {
        return this.axiosInstance.delete(url, config);
    }

    patch(url: string, payload: any, config?: AxiosRequestConfig<any>) {
        return this.axiosInstance.patch(url, payload, config);
    }

    public static getInstance(): AxiosService {
        if (!AxiosService.instance) {
            AxiosService.instance = new AxiosService();
        }
        return AxiosService.instance;
    }
}

const axiosService = AxiosService.getInstance();
export default axiosService;
